import React, { useState, useEffect } from "react";
import GlassCard from "../../components/GlassCard";
import { useNavigate } from "react-router-dom";
import { useCurrentLang } from "../../hooks/useCurrentLang";
import { feedbackFormContent } from "../data/feedbackFormContent"; 

const FEEDBACK_FIELDS = [
  {
    id: "feeling_before_session",
    labelKey: "fieldFeelingBefore",
    type: "scale",
    min: 1,
    max: 6,
  },
  {
    id: "feeling_after_session",
    labelKey: "fieldFeelingAfter",
    type: "scale",
    min: 1,
    max: 6,
  },
  {
    id: "total_effort",
    labelKey: "fieldTotalEffort",
    type: "scale",
    min: 1,
    max: 6,
  },
];

const FeedbackForm = (exercise_props) => {
  // Expecting something like: { exercises: Exercise[], onSubmit: fn, onClose: fn }
  const { exercises = [], onSubmit, onClose } = exercise_props || {};

  const navigate = useNavigate();
  const currentLang = useCurrentLang();

  // Normalize to "en" / "sl" only
  const lang = currentLang === "sl" ? "sl" : "en";
  const t = feedbackFormContent[lang];

  const [formState, setFormState] = useState({});
  const [stepError, setStepError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Initialize default values for scale fields and exercise enjoyment
  useEffect(() => {
    setStepError("");
    setFormState((prev) => {
      const next = { ...prev };

      // Static session-level scales
      FEEDBACK_FIELDS.forEach((field) => {
        if (field.type === "scale" && next[field.id] === undefined) {
          next[field.id] = field.min;
        }
      });

      // Dynamic per-exercise fields
      exercises.forEach((exercise, index) => {
        const baseId = `exercise_${exercise.id || index}`;
        const difficultyId = `${baseId}_difficulty`;
        const enjoymentId = `${baseId}_enjoyment`;

        if (next[difficultyId] === undefined) {
          next[difficultyId] = "";
        }
        if (next[enjoymentId] === undefined) {
          next[enjoymentId] = 1;
        }
      });

      return next;
    });
  }, [exercises]);

  const toggleMultiSelectValue = (fieldId, value) => {
    setStepError("");
    setFormState((prev) => {
      const currentValues = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [fieldId]: nextValues,
      };
    });
  };

  const handleFieldChange = (fieldId, value) => {
    setStepError("");
    setFormState((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const buildNullFeedbackState = () => {
    const nullState = {};

    // Session-level fields
    FEEDBACK_FIELDS.forEach((field) => {
      nullState[field.id] = null;
    });

    // Per-exercise difficulty and enjoyment
    exercises.forEach((exercise, index) => {
      const baseId = `exercise_${exercise.id || index}`;
      const difficultyId = `${baseId}_difficulty`;
      const enjoymentId = `${baseId}_enjoyment`;

      nullState[difficultyId] = null;
      nullState[enjoymentId] = null;
    });

    return nullState;
  };

  const renderScaleField = (field) => {
    const currentValue =
      formState[field.id] === undefined ? field.min : formState[field.id];

    const labelText = field.labelKey ? t[field.labelKey] : "";

    return (
      <div key={field.id} className="intake-question-block">
        <label className="intake-question-label" htmlFor={field.id}>
          {labelText} <span className="intake-required-star">*</span>
        </label>

        <div className="intake-scale-wrap">
          <input
            id={field.id}
            type="range"
            min={field.min}
            max={field.max}
            step="1"
            value={currentValue}
            className="intake-range"
            onChange={(e) =>
              handleFieldChange(field.id, Number(e.target.value))
            }
          />
          <div className="intake-scale-row">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <span
                key={num}
                className={`intake-scale-pill ${
                  Number(currentValue) === num ? "active" : ""
                }`}
              >
                {num}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderMultiSelectField = (field) => (
    <div key={field.id} className="intake-question-block">
      <div className="intake-question-label">
        {field.label} <span className="intake-required-star">*</span>
      </div>
      <div className="intake-chip-group">
        {field.options.map((option) => {
          const currentValues = Array.isArray(formState[field.id])
            ? formState[field.id]
            : [];
          const isActive = currentValues.includes(option.value);

          return (
            <button
              key={option.value}
              type="button"
              className={`intake-chip ${isActive ? "active" : ""}`}
              onClick={() => toggleMultiSelectValue(field.id, option.value)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderBooleanField = (field) => (
    <div key={field.id} className="intake-question-block">
      <div className="intake-question-label">
        {field.label} <span className="intake-required-star">*</span>
      </div>
      <div className="intake-radio-row">
        <label
          className={`intake-radio-card ${
            formState[field.id] === true ? "active" : ""
          }`}
        >
          <input
            type="radio"
            name={field.id}
            checked={formState[field.id] === true}
            onChange={() => handleFieldChange(field.id, true)}
          />
          <span>{t.yes}</span>
        </label>

        <label
          className={`intake-radio-card ${
            formState[field.id] === false ? "active" : ""
          }`}
        >
          <input
            type="radio"
            name={field.id}
            checked={formState[field.id] === false}
            onChange={() => handleFieldChange(field.id, false)}
          />
          <span>{t.no}</span>
        </label>
      </div>
    </div>
  );

  const renderExerciseFeedback = () => {
    if (!exercises || exercises.length === 0) {
      return null;
    }

    return (
      <div className="intake-fields-stack">
        <div className="intake-placeholder-card">
          <h3>{t.perExerciseTitle}</h3>
          <p>{t.perExerciseText}</p>
        </div>

        {exercises.map((exercise, index) => {
          const baseId = `exercise_${exercise.id || index}`;
          const difficultyId = `${baseId}_difficulty`;
          const enjoymentId = `${baseId}_enjoyment`;

          const exerciseName =
            exercise.name || exercise.title || `Exercise ${index + 1}`;

          return (
            <div key={baseId}>
              <div className="intake-question-block">
                <p className="intake-section-intro">{exerciseName}</p>
              </div>

              {/* Difficulty select: easy / medium / hard */}
              <div className="intake-question-block">
                <label
                  className="intake-question-label"
                  htmlFor={difficultyId}
                >
                  {t.exerciseDifficultyLabel}
                  <span className="intake-required-star">*</span>
                </label>
                <select
                  id={difficultyId}
                  className="intake-select"
                  value={formState[difficultyId] || ""}
                  onChange={(e) =>
                    handleFieldChange(difficultyId, e.target.value)
                  }
                >
                  <option value="">{t.exerciseDifficultyPlaceholder}</option>
                  <option value="easy">{t.exerciseDifficultyEasy}</option>
                  <option value="medium">{t.exerciseDifficultyMedium}</option>
                  <option value="hard">{t.exerciseDifficultyHard}</option>
                </select>
              </div>

              {/* Enjoyment scale 1–6 */}
              {renderScaleField({
                id: enjoymentId,
                labelKey: "exerciseEnjoymentLabel",
                type: "scale",
                min: 1,
                max: 6,
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const isFormValid = () => {
    // Require all static fields + per-exercise difficulty to be filled
    const requiredIds = [
      ...FEEDBACK_FIELDS.map((field) => field.id),
      ...exercises.map((exercise, index) => {
        const baseId = `exercise_${exercise.id || index}`;
        return `${baseId}_difficulty`;
      }),
    ];

    return requiredIds.every((id) => {
      const value = formState[id];
      return value !== undefined && value !== "";
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStepError("");
    setSubmitError("");

    if (!isFormValid()) {
      setStepError(t.validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Allow parent (SessionPage) to handle actual persistence
      if (typeof onSubmit === "function") {
        await onSubmit({
          feedback: formState,
          exercises,
          lang: currentLang,
        });
      }

      if (typeof onClose === "function") {
        onClose();
      }
    } catch (error) {
      console.error("Feedback submit error:", error);
      setSubmitError(t.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = async () => {
    setStepError("");
    setSubmitError("");
    setIsSubmitting(true);

    try {
      if (typeof onSubmit === "function") {
        const nullFeedback = buildNullFeedbackState();
        await onSubmit({
          feedback: nullFeedback,
          exercises,
          lang: currentLang,
          // optional: you can add a flag the backend can use:
          // skipped: true,
        });
      }

      if (typeof onClose === "function") {
        onClose();
      }
    } catch (error) {
      console.error("Feedback cancel submit error:", error);
      setSubmitError(t.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section className="feedback-form">
      {/* You will mount this inside your modal in SessionPage.jsx */}
      <div className="intake-page-shell">
        <div className="intake-form-wrap">
          <form onSubmit={handleSubmit}>
            <GlassCard className="intake-glass-card">
              <div className="intake-card-inner">
                <div className="intake-card-top">
                  <div className="intake-top-row">
                    <div className="intake-step-meta">
                      <span className="intake-step-counter">
                        {t.headerBadge}
                      </span>
                      <h2 className="intake-step-title">
                        {t.headerTitle}
                      </h2>
                    </div>
                  </div>
                </div>

                <div className="intake-card-body">
                  <div className="intake-screen-content">
                    <h1 className="intake-main-title">
                      {t.mainTitle}
                    </h1>
                    <p className="intake-lead">{t.mainLead}</p>

                    <div className="intake-fields-stack">
                      <div className="intake-placeholder-card">
                        <h3>{t.overallTitle}</h3>
                        <p>{t.overallText}</p>
                      </div>

                      {FEEDBACK_FIELDS.map((field) => {
                        if (field.type === "scale") {
                          return renderScaleField(field);
                        }
                        return null;
                      })}
                    </div>

                    {renderExerciseFeedback()}

                    {stepError && (
                      <div className="intake-step-error" role="alert">
                        {stepError}
                      </div>
                    )}
                    {submitError && (
                      <div className="intake-step-error" role="alert">
                        {submitError}
                      </div>
                    )}
                  </div>
                </div>

                <div className="intake-card-footer">
                  <button
                    type="submit"
                    className="intake-primary-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t.submitSaving : t.submitText}
                  </button>
                  {typeof onClose === "function" && (
                    <button
                      type="button"
                      className="intake-primary-button"
                      style={{ marginLeft: "12px", background: "transparent" }}
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      {t.cancelText}
                    </button>
                  )}
                </div>
              </div>
            </GlassCard>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FeedbackForm;